<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { PersonResponse } from '@/types/personProfile'
import type { crunchbaseData } from '@/types/company'
import { myRequest } from '@/utils/fetch'
import { useRoute } from 'vue-router'

type Props = {
  person: PersonResponse
  ready: boolean
  showGenerate: boolean
  showAllSections: boolean
  enrichmentFilters?: {
    currentPosition: boolean
    jobHistory: boolean
    education: boolean
    companyOverview: boolean
    companyNews: boolean
    companyFunding: boolean
    weather: boolean
    talkingPoints: boolean
    location: boolean
  }
}
const route = useRoute()

const props = defineProps<Props>()

// Add pagination state for news
const currentNewsPage = ref(0)
const newsPerPage = 5

// Add computed property for paginated news
const paginatedNews = computed(() => {
  if (!props.person?.Company?.news) return []
  const start = currentNewsPage.value * newsPerPage
  return props.person.Company.news.slice(start, start + newsPerPage)
})

const totalNewsPages = computed(() => {
  if (!props.person?.Company?.news) return 0
  return Math.ceil(props.person.Company.news.length / newsPerPage)
})

// Add methods for pagination
const nextNewsPage = () => {
  if (currentNewsPage.value < totalNewsPages.value - 1) {
    currentNewsPage.value++
  }
}

const prevNewsPage = () => {
  if (currentNewsPage.value > 0) {
    currentNewsPage.value--
  }
}

// Create a separate ref for talking points
const talkingPoints = ref<PersonResponse['talkingPoints']>(props.person?.talkingPoints || [])

// Add selected categories state
const selectedCategories = ref<string[] | null>([])

const filteredTalkingPoints = computed(() => {
  if (!talkingPoints.value) return []
  if (
    selectedCategories.value === null ||
    selectedCategories.value?.length === 0 ||
    (uniqueCategories.value.length > 0 && selectedCategories.value?.length === uniqueCategories.value.length)
  ) {
    return talkingPoints.value
  }

  return talkingPoints.value.filter((point) =>
    point.categories?.some((category) => selectedCategories.value?.includes(category))
  )
})

// Watch for changes in uniqueCategories and initialize selected categories
const uniqueCategories = computed(() => {
  return [...new Set(talkingPoints.value?.flatMap((point) => point.categories) || [])]
})

// Initialize selected categories when uniqueCategories changes
watch(
  uniqueCategories,
  (newCategories) => {
    selectedCategories.value = []
  },
  { immediate: true }
)

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const extractDomain = (url: string): string => {
  try {
    const hostname = new URL(url).hostname
    // Extract only the domain without subdomains
    const parts = hostname.split('.')
    const domain = parts.length >= 2 ? parts.slice(-2).join('.') : hostname
    return domain
  } catch (err) {
    return url
  }
}

// Add proxy image function
const getProxiedImageUrl = (url: string): string => {
  if (!url) return ''
  // If the URL is already from our backend, return as is
  if (url.startsWith('/')) return url
  // Otherwise, proxy through our backend
  return `${import.meta.env.VITE_API_URL}/app/proxy-image?url=${encodeURIComponent(url)}`
}

const timestampFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hour12: true
})

const formatDateRange = (position: {
  date: {
    start: { month?: number | null; year: number }
    end?: { month?: number | null; year: number }
  }
}) => {
  const start = position.date.start.month
    ? `${position.date.start.month}/${position.date.start.year}`
    : position.date.start.year

  const end = !position.date.end
    ? 'current'
    : position.date.end.month
      ? `${position.date.end.month}/${position.date.end.year}`
      : position.date.end.year

  if (start === 0 && end === 0) return undefined
  if (start === 0) return String(end)
  if (end === 0) return String(start)
  return `${start} - ${end}`
}

const sortedWorkExperience = computed(() => {
  const allPositions =
    props.person?.enrichmentData?.workExperince?.flatMap((company) =>
      company.profile_positions.map((position) => ({
        ...position,
        companyName: company.company.name,
        companyId: company.company.id,
        companyLogo: company.company.logo
      }))
    ) || []

  return allPositions.sort((a, b) => {
    if (!a.date.end && b.date.end) return -1
    if (a.date.end && !b.date.end) return 1

    const aStartDate = new Date(`${a.date.start.year}-${a.date.start.month || 1}`).getTime()
    const bStartDate = new Date(`${b.date.start.year}-${b.date.start.month || 1}`).getTime()
    if (aStartDate !== bStartDate) return bStartDate - aStartDate

    if (!a.date.end && !b.date.end) return 0
    if (!a.date.end) return -1
    if (!b.date.end) return 1

    const aEndDate = new Date(`${a.date.end.year}-${a.date.end.month || 1}`).getTime()
    const bEndDate = new Date(`${b.date.end.year}-${b.date.end.month || 1}`).getTime()
    return bEndDate - aEndDate
  })
})

const crunchbaseInfo = computed(() => {
  const data = props.person?.Company?.crunchbase as crunchbaseData
  if (data?.founding) {
    data.founding.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }
  return data
})

// Add dialog state and additional context
const showGenerateDialog = ref(false)
const additionalContext = ref('')
const isGenerating = ref(false)

// Add generateTalkingPoints function
const generateTalkingPoints = async () => {
  showGenerateDialog.value = true
}
const handleGenerate = async () => {
  try {
    isGenerating.value = true
    const response = await myRequest('POST', `/ai/talking-points/${props.person.personID}/${route.params.eventID}`, {
      context: additionalContext.value
    })
    talkingPoints.value = response
    showGenerateDialog.value = false
    additionalContext.value = ''
  } catch (error) {
    console.error('Failed to generate talking points:', error)
  } finally {
    isGenerating.value = false
  }
}
</script>

<template>
  <div class="row justify-between q-col-gutter-md">
    <div class="col-12" v-if="person?.talkingPoints?.length && (showAllSections || enrichmentFilters?.talkingPoints)">
      <div class="myCard q-pa-md">
        <h6 class="row justify-between items-center q-mb-md">
          <div><q-avatar icon="mdi-robot" text-color="primary" /> AI-Generated Discussion Points</div>
          <div class="row items-center">
            <div v-if="uniqueCategories.length > 0">
              <q-select
                v-model="selectedCategories"
                :options="[...uniqueCategories]"
                multiple
                filled
                dense
                clearable
                label="Filter by category"
                class="q-mr-sm"
                style="width: 200px"
                :display-value="
                  selectedCategories && selectedCategories.length > 0
                    ? selectedCategories.length === 1
                      ? selectedCategories[0].length > 15
                        ? selectedCategories[0].substring(0, 15) + '...'
                        : selectedCategories[0]
                      : `${selectedCategories.length} categories`
                    : undefined
                "
              >
              </q-select>
            </div>
            <q-btn
              size="md"
              icon="mdi-creation-outline"
              color="primary"
              @click="generateTalkingPoints"
              class="shiny-button"
              :loading="isGenerating"
              :disable="isGenerating"
              v-show="showGenerate"
            >
              <q-tooltip>Generate Discussion Points</q-tooltip>
            </q-btn>
          </div>
        </h6>
        <q-list separator style="flex-shrink: 0; max-height: 300px; overflow-y: auto" class="scrollable-area">
          <template v-if="isGenerating">
            <q-item v-for="n in 3" :key="n">
              <q-item-section>
                <q-skeleton type="text" width="85%" />
                <q-skeleton type="text" width="60%" class="q-mt-xs" />
              </q-item-section>
              <q-item-section side>
                <q-skeleton type="QBtn" />
              </q-item-section>
            </q-item>
          </template>
          <template v-else>
            <q-item v-for="talkingPoint in filteredTalkingPoints" :key="talkingPoint.talkingPoint">
              <q-item-section>
                <q-item-label>{{ talkingPoint.talkingPoint }}</q-item-label>
                <q-item-label caption class="q-mt-xs">
                  <div class="row items-center q-gutter-x-xs">
                    <template v-if="talkingPoint.categories && talkingPoint.categories.length">
                      <q-chip
                        v-for="category in talkingPoint.categories"
                        :key="category"
                        size="sm"
                        color="primary"
                        text-color="white"
                      >
                        {{ category }}
                      </q-chip>
                    </template>
                    <template v-if="talkingPoint.sources && talkingPoint.sources.length">
                      <q-btn
                        v-for="source in talkingPoint.sources"
                        :key="source"
                        type="a"
                        :href="source"
                        target="_blank"
                        flat
                        no-caps
                        size="sm"
                        color="blue"
                        class="q-ml-sm"
                      >
                        <q-icon name="mdi-link-variant" size="xs" class="q-mr-xs" />
                        {{ extractDomain(source) }}
                      </q-btn>
                    </template>
                  </div>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-btn
                  flat
                  round
                  dense
                  icon="mdi-content-copy"
                  @click="copyToClipboard(talkingPoint.talkingPoint)"
                  size="sm"
                >
                  <q-tooltip>Copy to clipboard</q-tooltip>
                </q-btn>
              </q-item-section>
            </q-item>
          </template>
        </q-list>
      </div>
    </div>
    <div class="col-12 col-md" style="min-width: 40%">
      <div class="myCard q-pa-md full-height">
        <transition appear enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
          <div v-if="ready">
            <h6><q-avatar icon="mdi-account" /> Job title & Description</h6>
            <div class="row justify-between">
              <q-img
                class="col-12"
                :class="{
                  'col-sm-4': !person?.weather,
                  'col-sm-6': person?.weather
                }"
                v-if="person?.profilePicutre"
                :src="person?.profilePicutre"
                alt="avatar"
                style="border-radius: 4px"
              >
                <template v-slot:error>
                  <q-icon name="mdi-account" size="lg" />
                </template>
              </q-img>
              <q-icon
                class="col-12"
                :class="{
                  'col-sm-4': !person?.weather,
                  'col-sm-6': person?.weather
                }"
                v-if="!person?.profilePicutre"
                name="mdi-account"
                size="lg"
              />
              <div
                class="self-center col-12 q-pa-md"
                :class="{
                  'col-sm-8': !person?.weather,
                  'col-sm-6': person?.weather
                }"
              >
                <template v-if="showAllSections || enrichmentFilters?.currentPosition">
                  <q-avatar size="70px" rounded>
                    <q-img :src="person?.enrichmentData?.workExperince[0].company.logo" alt="avatar" />
                  </q-avatar>
                  {{ person?.enrichmentData?.workExperince[0].company.name }} <br /><br />
                  {{ person?.enrichmentData?.jobTitle }} <br />
                  <span class="q-timeline__subtitle">{{
                    person?.enrichmentData?.currentJobMonth +
                    '/' +
                    person?.enrichmentData?.currentJobYear +
                    ' - current'
                  }}</span>
                  <br />
                </template>
                <span v-if="person?.location && (showAllSections || enrichmentFilters?.location)">
                  <q-icon name="mdi-map-marker" /> {{ person?.location }}
                </span>
                <div v-if="person?.weather && (showAllSections || enrichmentFilters?.weather)" class="q-mt-md">
                  <div class="row items-center q-gutter-x-md">
                    <div class="text-caption">
                      <q-img :src="person.weather.icon" style="display: inline-block; width: 30px" />
                      {{ person.weather.condition }}
                    </div>
                    <div class="col-auto">
                      <q-icon name="mdi-thermometer" size="sm" class="text-orange" />
                      <span class="text-caption">
                        {{ person.weather.temperature.f.current }}°F / {{ person.weather.temperature.c.current }}°C
                      </span>
                    </div>
                  </div>
                  <div class="row q-gutter-x-md">
                    <div class="col-auto">
                      <q-icon name="mdi-thermometer-high" size="sm" class="text-red" />
                      <span class="text-caption"
                        >High: {{ person.weather.temperature.f.max }}°F / {{ person.weather.temperature.c.max }}°C</span
                      >
                    </div>
                    <div class="col-auto">
                      <q-icon name="mdi-thermometer-low" size="sm" class="text-blue" />
                      <span class="text-caption"
                        >Low: {{ person.weather.temperature.f.min }}°F / {{ person.weather.temperature.c.min }}°C</span
                      >
                    </div>
                  </div>
                  <div class="row q-gutter-x-md">
                    <div class="col-auto">
                      <q-icon name="mdi-water-percent" size="sm" class="text-cyan" />
                      <span class="text-caption">{{ person.weather.humidity }}% humidity</span>
                    </div>
                    <div class="col-auto">
                      <q-icon name="mdi-weather-windy" size="sm" class="text-grey" />
                      <span class="text-caption">
                        {{ person.weather.windSpeed.f }} mph / {{ person.weather.windSpeed.c }} kph
                      </span>
                    </div>
                  </div>
                  <div class="row q-gutter-x-md">
                    <div class="col-auto" v-if="person.weather.chanceOfRain > 0">
                      <q-icon name="mdi-weather-rainy" size="sm" class="text-blue" />
                      <span class="text-caption"> {{ person.weather.chanceOfRain }}% rain</span>
                    </div>
                    <div class="col-auto" v-if="person.weather.chanceOfSnow > 0">
                      <q-icon name="mdi-weather-snowy" size="sm" class="text-blue-grey" />
                      <span class="text-caption"> {{ person.weather.chanceOfSnow }}% snow</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <br />
            <p class="myEllipsis" style="-webkit-line-clamp: 10">
              {{ person?.enrichmentData?.summary }}
              <q-tooltip class="text-body2">{{ person?.enrichmentData?.summary }}</q-tooltip>
            </p>
          </div>
        </transition>
      </div>
    </div>
    <div class="col-12 col-md" style="min-width: 40%" v-if="showAllSections || enrichmentFilters?.jobHistory">
      <div class="myCard q-pa-md full-height">
        <h6 class="row justify-between items-center">
          <div><q-avatar icon="mdi-briefcase" /> Work Experince</div>
          <q-btn
            v-if="person?.linkedinUsername"
            flat
            dense
            round
            type="a"
            :href="`https://linkedin.com/in/${person.linkedinUsername}`"
            target="_blank"
            icon="mdi-linkedin"
            color="blue"
          >
            <q-tooltip>LinkedIn Profile</q-tooltip>
          </q-btn>
        </h6>
        <q-timeline color="secondary" v-if="ready">
          <q-timeline-entry
            v-for="record in sortedWorkExperience"
            :key="record.companyId"
            :title="`${record.companyName} - ${record.title}`"
            :subtitle="formatDateRange(record)"
            :avatar="record.companyLogo"
          >
            <div class="myEllipsis">
              {{ record.description }}
              <q-tooltip class="text-body2">{{ record.description }}</q-tooltip>
            </div>
          </q-timeline-entry>
        </q-timeline>
      </div>
    </div>
    <div
      v-if="
        person?.Company?.news && person?.Company?.news.length > 0 && (showAllSections || enrichmentFilters?.companyNews)
      "
      class="col-12 col-md"
      style="min-width: 40%"
    >
      <div class="myCard q-pa-md full-height">
        <h6><q-avatar icon="mdi-newspaper" /> Company news</h6>
        <transition appear enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
          <div>
            <q-list separator>
              <q-item clickable v-ripple v-for="news in paginatedNews" :key="news.title" :href="news.link">
                <q-item-section avatar v-if="news.image">
                  <q-img :src="getProxiedImageUrl(news.image)" :ratio="16 / 9" style="width: 100px; border-radius: 4px">
                    <template v-slot:error>
                      <div class="absolute-full flex flex-center bg-grey-2 text-grey-8">
                        <q-icon name="mdi-image-off" size="sm" />
                      </div>
                    </template>
                  </q-img>
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ news.title }}</q-item-label>
                  <q-item-label caption
                    >{{ news.source }} {{ timestampFormatter.format(new Date(news.datetime)) }}</q-item-label
                  >
                </q-item-section>
              </q-item>
            </q-list>
            <div class="row justify-between items-center q-mt-md">
              <q-btn flat dense icon="mdi-chevron-left" :disable="currentNewsPage === 0" @click="prevNewsPage">
                <q-tooltip>Previous Page</q-tooltip>
              </q-btn>
              <div class="text-caption">Page {{ currentNewsPage + 1 }} of {{ totalNewsPages }}</div>
              <q-btn
                flat
                dense
                icon="mdi-chevron-right"
                :disable="currentNewsPage >= totalNewsPages - 1"
                @click="nextNewsPage"
              >
                <q-tooltip>Next Page</q-tooltip>
              </q-btn>
            </div>
          </div>
        </transition>
      </div>
    </div>
    <div
      v-if="person?.Company?.description && (showAllSections || enrichmentFilters?.companyOverview)"
      class="col-12 col-md"
      style="min-width: 40%"
    >
      <div class="myCard q-pa-md full-height">
        <h6 class="row justify-between items-center">
          <div><q-avatar icon="mdi-information" /> Company Summary</div>
          <q-btn
            v-if="person?.Company?.linkedin"
            flat
            dense
            round
            type="a"
            :href="person.Company.linkedin"
            target="_blank"
            icon="mdi-linkedin"
            color="blue"
          >
            <q-tooltip>LinkedIn Company Profile</q-tooltip>
          </q-btn>
        </h6>

        <transition appear enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
          <p>{{ person?.Company?.description.substring(0, 300) }}</p>
        </transition>
      </div>
    </div>
    <div
      v-if="
        person?.Company?.crunchbase &&
        (crunchbaseInfo.founders?.length || crunchbaseInfo.founding?.length || crunchbaseInfo.investors?.length) &&
        (showAllSections || enrichmentFilters?.companyFunding)
      "
      class="col-12 col-md"
      style="min-width: 40%"
    >
      <div class="myCard q-pa-md full-height">
        <h6><q-avatar icon="mdi-chart-line" /> Company Funding</h6>
        <transition appear enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
          <div>
            <template v-if="crunchbaseInfo.founders?.length">
              <div class="q-mb-md">
                <div class="text-bold">Founders</div>
                <q-list dense>
                  <q-item v-for="founder in crunchbaseInfo.founders" :key="founder.name">
                    <q-item-section>
                      <q-item-label>{{ founder.name }}</q-item-label>
                      <q-item-label caption>{{ founder.role }}</q-item-label>
                    </q-item-section>
                    <q-item-section side v-if="founder.sources?.length">
                      <div class="row items-center no-wrap">
                        <q-btn
                          v-for="(source, index) in founder.sources"
                          :key="source"
                          type="a"
                          :href="source"
                          target="_blank"
                          flat
                          dense
                          no-caps
                          size="sm"
                          color="blue"
                          class="q-ml-xs"
                        >
                          <template v-if="founder.sources.length === 1">
                            <q-icon name="mdi-link-variant" size="xs" />
                            <q-tooltip>{{ extractDomain(source) }}</q-tooltip>
                          </template>
                          <template v-else>
                            {{ index + 1 }}
                            <q-tooltip>{{ extractDomain(source) }}</q-tooltip>
                          </template>
                        </q-btn>
                      </div>
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
            </template>

            <template v-if="crunchbaseInfo.founding?.length">
              <div class="q-mb-md">
                <div class="text-bold">Funding History</div>
                <q-timeline color="secondary" dense>
                  <q-timeline-entry v-for="round in crunchbaseInfo.founding" :key="round.date" :subtitle="round.date">
                    <template v-slot:title>
                      <div class="row justify-between">
                        <h5>{{ round.type }}: {{ round.amount === 0 ? 'Unknown' : `$${round.amount}M` }}</h5>
                        <div>
                          <div v-if="round.sources?.length" class="row items-center q-gutter-x-xs">
                            <div class="row items-center no-wrap">
                              <q-btn
                                v-for="(source, index) in round.sources"
                                :key="source"
                                type="a"
                                :href="source"
                                target="_blank"
                                flat
                                dense
                                no-caps
                                size="sm"
                                color="blue"
                              >
                                <template v-if="round.sources.length === 1">
                                  <q-icon name="mdi-link-variant" size="xs" />
                                  <q-tooltip>{{ extractDomain(source) }}</q-tooltip>
                                </template>
                                <template v-else>
                                  {{ index + 1 }}
                                  <q-tooltip>{{ extractDomain(source) }}</q-tooltip>
                                </template>
                              </q-btn>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </q-timeline-entry>
                </q-timeline>
              </div>
            </template>

            <template v-if="crunchbaseInfo.investors?.length">
              <div class="q-mb-md">
                <div class="text-bold">Key Investors</div>
                <q-chip
                  v-for="investor in crunchbaseInfo.investors"
                  :key="typeof investor === 'string' ? investor : investor.investor"
                  color="primary"
                  text-color="white"
                  class="q-ma-xs"
                >
                  {{ typeof investor === 'string' ? investor : investor.investor }}
                  <template v-if="typeof investor !== 'string' && investor.sources?.length">
                    <div class="row items-center no-wrap">
                      <q-btn
                        v-for="(source, index) in investor.sources"
                        :key="source"
                        type="a"
                        :href="source"
                        target="_blank"
                        flat
                        dense
                        no-caps
                        size="sm"
                        color="white"
                        class="q-ml-xs"
                      >
                        <template v-if="investor.sources.length === 1">
                          <q-icon name="mdi-link-variant" size="xs" />
                          <q-tooltip>{{ extractDomain(source) }}</q-tooltip>
                        </template>
                        <template v-else>
                          {{ index + 1 }}
                          <q-tooltip>{{ extractDomain(source) }}</q-tooltip>
                        </template>
                      </q-btn>
                    </div>
                  </template>
                </q-chip>
              </div>
            </template>
          </div>
        </transition>
      </div>
    </div>
    <div
      v-if="person?.enrichmentData?.education && (showAllSections || enrichmentFilters?.education)"
      class="col-12 col-md"
      style="min-width: 40%"
    >
      <div class="myCard q-pa-md full-height">
        <h6><q-avatar icon="mdi-school" /> Education</h6>
        <transition appear enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
          <q-timeline color="accent">
            <q-timeline-entry
              v-for="record in person?.enrichmentData?.education"
              :key="record.school.name"
              :title="record.school.name"
              :subtitle="formatDateRange(record)"
            >
              <div>{{ record.field_of_study.join(' - ') }}</div>
            </q-timeline-entry>
          </q-timeline>
        </transition>
      </div>
    </div>
    <div v-if="person?.enrichmentData?.languages?.length" class="col-12 col-md" style="min-width: 40%">
      <div class="myCard q-pa-md full-height">
        <h6><q-avatar icon="mdi-translate-variant" /> Languages</h6>
        <transition appear enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
          <q-list separator>
            <q-item v-for="record in person?.enrichmentData?.languages" :key="record.name">
              <q-item-section>
                <q-item-label>{{ record.name }}</q-item-label>
                <q-item-label caption>{{ record.proficiency }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </transition>
      </div>
    </div>
    <!-- Add Generate Talking Points Dialog -->
    <q-dialog v-model="showGenerateDialog" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">Generate New Discussion Points</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-input
            v-model="additionalContext"
            label="Additional Context (Optional)"
            type="textarea"
            filled
            autogrow
            hint="Add any specific topics or areas you'd like the AI to focus on"
          />
        </q-card-section>

        <q-card-actions align="right" class="text-primary">
          <q-btn flat label="Cancel" v-close-popup />
          <q-btn
            color="primary"
            label="Generate"
            :loading="isGenerating"
            :disable="isGenerating"
            @click="handleGenerate"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<style>
/* WebKit-based browsers (Chrome, Edge, Safari) */
.scrollable-area::-webkit-scrollbar {
  width: 10px;
}

.scrollable-area::-webkit-scrollbar-track {
  background: transparent;
}

.scrollable-area::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.scrollable-area::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.4);
}

/* Firefox */
.scrollable-area {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

/* Shiny button animation */
.shiny-button {
  position: relative;
  overflow: hidden;
}

.shiny-button::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: rotate(45deg);
  animation: shine 3s infinite;
  pointer-events: none;
}

@keyframes shine {
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  20%,
  100% {
    transform: translateX(100%) rotate(45deg);
  }
}
</style>
